import styled from "@emotion/styled";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";

import { ErrorDialog, FONT_WEIGHT_MEDIUM } from "@skilitics/designer-ui-components";
import { useDynamicFormatString, useFormatMessage, type FormatStrings } from "@skilitics/localization";

import { usePersistenceStatus } from "./persistenceStatus";

type Props = {
  children: Exclude<React.ReactNode, undefined | null>;
  onDiscard?: () => void;
};

type MessageUse = "heading" | "text";
type MessageStatus = "retrying" | "retriesExhausted" | "failed";
const messageStatuses: MessageStatus[] = ["retrying", "retriesExhausted", "failed"];

const isMessageStatus = (status: string): status is MessageStatus => {
  return messageStatuses.includes(status as MessageStatus);
};

type FormatMessages = {
  [name in `${MessageUse}${Capitalize<MessageStatus>}`]: FormatStrings;
} & {
  close: FormatStrings;
  discardChanges: FormatStrings;
  timeSinceLastSavedChange: FormatStrings;
  retryNow: FormatStrings;
};

// copilot translations... :|
const formatStrings: FormatMessages = {
  headingRetrying: {
    en: "Failed while saving changes. Retrying...",
    de: "Fehler beim Speichern der Änderungen. Wiederhole den Vorgang...",
    es: "Error al guardar los cambios. Reintentando...",
    fr: "Échec de l'enregistrement des modifications. Nouvelle tentative...",
  },
  textRetrying: {
    en: "An error occurred while attempting to save recent changes. Retrying in {retryInSeconds} seconds.",
    de: "Beim Versuch, die letzten Änderungen zu speichern, ist ein Fehler aufgetreten. Wiederhole den Vorgang in {retryInSeconds} Sekunden.",
    es: "Se produjo un error al intentar guardar los cambios recientes. Reintentando en {retryInSeconds} segundos.",
    fr: "Une erreur s'est produite lors de la tentative d'enregistrement des modifications récentes. Nouvelle tentative dans {retryInSeconds} secondes.",
  },
  headingRetriesExhausted: {
    en: "Failed to save changes after multiple attempts.",
    de: "Die Änderungen konnten nach mehreren Versuchen nicht gespeichert werden.",
    es: "No se pudieron guardar los cambios después de varios intentos.",
    fr: "Échec de l'enregistrement des modifications après plusieurs tentatives.",
  },
  textRetriesExhausted: {
    en: "Failed to save changes after {attemptCount} attempts.",
    de: "Die Änderungen konnten nach {attemptCount} Versuchen nicht gespeichert werden.",
    es: "No se pudieron guardar los cambios después de {attemptCount} intentos.",
    fr: "Échec de l'enregistrement des modifications après {attemptCount} tentatives.",
  },
  headingFailed: {
    en: "Changes could not be saved.",
    de: "Die Änderungen konnten nicht gespeichert werden.",
    es: "No se pudieron guardar los cambios.",
    fr: "Les modifications n'ont pas pu être enregistrées.",
  },
  textFailed: {
    en: "An error occurred while trying to save changes...",
  },
  retryNow: { en: "Retry Now", de: "Jetzt wiederholen", es: "Reintentar Ahora", fr: "Réessayer Maintenant" },
  timeSinceLastSavedChange: {
    en: "To resolve this issue, try reloading the page. Note that this will lose unsaved changes from up to {oldestRevisionTimestamp} ago.",
  },
  close: {
    en: "Close and Ignore",
  },
  discardChanges: {
    en: "Discard Changes",
    de: "Änderungen verwerfen",
    es: "Descartar cambios",
    fr: "Annuler les modifications",
  },
};

interface ReloadPageSinceProps {
  timestamp: Date;
}

const TimeSinceLastSavedChangeMessage = ({ timestamp }: ReloadPageSinceProps) => {
  const formatMessage = useFormatMessage(formatStrings);
  const [formattedSince, setFormattedSince] = useState(() => formatDistanceToNow(timestamp));

  useEffect(() => {
    const interval = setInterval(() => {
      setFormattedSince(formatDistanceToNow(timestamp));
    }, 30 * 1000);

    return () => clearInterval(interval);
  }, [timestamp]);

  return (
    <EmphasizedMessage>
      {formatMessage("timeSinceLastSavedChange", {
        oldestRevisionTimestamp: formattedSince,
      })}
    </EmphasizedMessage>
  );
};

const EmphasizedMessage = styled.div`
  background: ${({ theme }) => theme.primaryContainer};
  color: ${({ theme }) => theme.onPrimaryContainer};
  font-weight: ${FONT_WEIGHT_MEDIUM};
  padding: 10px;
  border-radius: 4px;
`;

export const UnsavedChangesHandler = ({ children, onDiscard }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const formatDynamicMessage = useDynamicFormatString();

  const persistenceStatus = usePersistenceStatus();

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // prompt if there are unsaved changes so they can cancel
      // note that we can't change the message via this API
      // https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
      // I note that Azure Portal somehow manages a custom popup in the same situation that works in Firefox even
      if (persistenceStatus.type !== "idle") {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [persistenceStatus.type]);

  let dialog: React.ReactNode = null;

  if (isMessageStatus(persistenceStatus.type)) {
    switch (persistenceStatus.type) {
      case "retriesExhausted": {
        const { errorDetails } = persistenceStatus;
        dialog = (
          <ErrorDialog
            title={formatMessage("headingRetriesExhausted")}
            text={
              <>
                <div>
                  {formatMessage("textRetriesExhausted", {
                    attemptCount: persistenceStatus.attemptCount,
                  })}
                </div>
                <div>{formatDynamicMessage(errorDetails.message)}</div>
                <TimeSinceLastSavedChangeMessage timestamp={errorDetails.startedAt} />
              </>
            }
            buttonsProps={[
              ...(onDiscard
                ? [{ label: formatMessage("discardChanges"), onClick: onDiscard, color: "error" as const }]
                : []),
              {
                label: formatMessage("close"),
                onClick: persistenceStatus.dismiss,
              },
              {
                label: formatMessage("retryNow"),
                onClick: persistenceStatus.retry,
                color: "primary",
              },
            ]}
          />
        );
        break;
      }

      case "retrying": {
        dialog = (
          <ErrorDialog
            title={formatMessage("headingRetrying")}
            text={formatMessage("textRetrying", {
              // TODO: fancy counter or don't show.
              retryInSeconds: Math.round(persistenceStatus.retryInMs / 1000),
            })}
            buttonsProps={[
              {
                label: formatMessage("retryNow"),
                onClick: persistenceStatus.retryNow,
                color: "primary",
              },
            ]}
          />
        );
        break;
      }

      case "failed": {
        const { errorDetails } = persistenceStatus;
        dialog = (
          <ErrorDialog
            title={formatMessage("headingFailed")}
            text={
              <>
                <div>{formatMessage("textFailed")}</div>
                <div>{formatDynamicMessage(errorDetails.message)}</div>
                <div>
                  <TimeSinceLastSavedChangeMessage timestamp={errorDetails.startedAt} />
                </div>
              </>
            }
            buttonsProps={[
              ...(onDiscard
                ? [{ label: formatMessage("discardChanges"), onClick: onDiscard, color: "error" as const }]
                : []),
              {
                label: formatMessage("close"),
                onClick: persistenceStatus.dismiss,
              },
            ]}
          />
        );
        break;
      }
    }
  }

  return (
    <>
      {children}
      {dialog}
    </>
  );
};
